<template>
	<div
		ref="rootRef"
		class="cms-element-product-description-reviews flex flex-wrap mt-2.5 scroll-mt-32"
	>
		<div class="hidden sm:block w-full">
			<ProductProductDetailsDesktopTabs
				:tabs="tabs"
				:current-tab-hash="currentTabHash"
				:change-tab="changeTab"
				:description="currentTabDescription"
				:current-tab-specification="currentTabSpecification"
				:size-guide-image="sizeGuideImage"
				:washing-instruction-image="washingInstructionImage"
			/>
		</div>
		<div class="sm:hidden w-full">
			<ProductProductDetailsMobileAccordion
				:tabs="tabs"
				:current-tab-hash="currentTabHash"
				:change-tab="changeTab"
				:description="currentTabDescription"
				:current-tab-specification="currentTabSpecification"
				:size-guide-image="sizeGuideImage"
				:washing-instruction-image="washingInstructionImage"
				:reviews-open-request="reviewsOpenRequest"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import type { VariantParametric } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type {
	BlokProductDetails,
	BlokSizeGuideTab,
	BlokWashingInstructionsTab,
	Image
} from "~/types/storyblok/storyblok";
import { storeToRefs } from "pinia";
import { getVisibleSpecifications } from "~/layers/base/domain/product/specification";
import { SPECIFICATION_EXCLUDED_CODES } from "~/templates/breadandboxers/utils/productParametricCodes";

interface Props {
	productDetailsBlok?: BlokProductDetails;
	sizeGuideTab?: BlokSizeGuideTab;
	washingInstructionsTab?: BlokWashingInstructionsTab;
}

const props = defineProps<Props>();

const appStore = useAppStore();
const route = useRoute();
const { selectedProduct, getParentFlagIds } = storeToRefs(useProductStore());

const lipscoreEnabled = computed(() => {
	if (appStore.lipscore_api_key) {
		const enabled =
			appStore.lipscore_enable === null ||
			appStore.lipscore_enable === true ||
			String(appStore.lipscore_enable) === "true";
		return enabled;
	}
	return !!(appStore.topBar as any)?.lipscore_api_key;
});

const tabs = computed(() => {
	const base = [
		{
			name: ProductDetailsTabs.DESCRIPTION,
			value: ProductDetailsTabs.DESCRIPTION
		},
		{
			name: ProductDetailsTabs.SPECIFICATION,
			value: ProductDetailsTabs.SPECIFICATION
		},
		{
			name: ProductDetailsTabs.SIZE_GUIDE,
			value: ProductDetailsTabs.SIZE_GUIDE
		},
		{
			name: ProductDetailsTabs.WASHING_INSTRUCTIONS,
			value: ProductDetailsTabs.WASHING_INSTRUCTIONS
		}
	];
	if (lipscoreEnabled.value) {
		base.push({
			name: ProductDetailsTabs.REVIEWS,
			value: ProductDetailsTabs.REVIEWS
		});
	}
	return base;
});

const currentTabHash = ref<string>(ProductDetailsTabs.DESCRIPTION);
// Always populated: desktop tabs render only the current tab, and the mobile
// accordion keeps Description open while another panel is opened.
const currentTabDescription = computed(
	() => selectedProduct.value?.Description || ""
);
const currentTabSpecification = computed<VariantParametric[]>(() =>
	getVisibleSpecifications(
		selectedProduct.value?.Parametrics,
		SPECIFICATION_EXCLUDED_CODES
	)
);

onMounted(() => {
	if (
		route.query.lipscore_received === "1" &&
		route.query.lipscore_token &&
		lipscoreEnabled.value
	) {
		currentTabHash.value = ProductDetailsTabs.REVIEWS;
	}
});

const changeTab = (value: string) => {
	return (currentTabHash.value = value);
};

const rootRef = ref<HTMLElement | null>(null);
// Bumped by the review link to reopen the mobile Reviews panel.
const reviewsOpenRequest = ref(0);

// On mobile the Reviews panel is the last accordion item, below an open
// Description — scroll to its header, not to the top of the block.
const reviewsButtonSelector = `#button-${ProductDetailsTabs.REVIEWS}`;

// Remount (and so open) the mobile Reviews panel only when the accordion is
// the visible one and that panel is still closed. On desktop it is hidden, and
// remounting would load a second, invisible Lipscore review list.
const shouldReopenMobileReviews = (): boolean => {
	const button = rootRef.value?.querySelector(reviewsButtonSelector);
	return (
		!!button &&
		button.getClientRects().length > 0 &&
		button.getAttribute("aria-expanded") !== "true"
	);
};

useReviewsTabLink(
	() => {
		changeTab(ProductDetailsTabs.REVIEWS);
		if (shouldReopenMobileReviews()) reviewsOpenRequest.value += 1;
	},
	rootRef,
	{ preferSelector: reviewsButtonSelector }
);

const productFlagIds = computed((): string[] => {
	const seed = selectedProduct.value?.FlagIdSeed;

	if (seed)
		return seed
			.split(",")
			.map((id) => id.trim())
			.filter(Boolean);

	return getParentFlagIds.value;
});

const sizeGuideImage = computed((): Image | undefined => {
	const tab = props.sizeGuideTab;
	if (!tab?.items || tab?.items.length === 0) return undefined;

	const matchingItem = tab.items.find((item) =>
		productFlagIds.value.includes(item.id)
	);

	return matchingItem?.image;
});

const washingInstructionImage = computed((): Image | undefined => {
	const tab = props.washingInstructionsTab;
	if (!tab || !tab.items || tab.items.length === 0) return undefined;
	return tab.items[0]?.image;
});
</script>
